import Image from 'next/image';
import type { LinkProps } from 'next/link';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState } from 'react';
import toast from 'react-hot-toast';

import ProfileDefaultImg from '@/assets/DefaultProfileImg.webp';
import MindPalLogoIcon from '@/assets/logo-black.svg';
import MindPalLogo from '@/assets/mindpal-logo-black.svg';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { useLogout } from '@/hooks/useLogout';
import { useMindCoins, useMindCoinsCheckout } from '@/hooks/useMindCoins';
import { useProfile } from '@/hooks/useProfile';
import { useUserCurrentSubscription } from '@/hooks/useUserCurrentSubscription';

import Button from './Button';
import Icon from './Icon';
import Text from './Text';

const sidebarLinks: {
  icon: string;
  href: LinkProps['href'];
  name: string;
  authOnly?: boolean;
}[] = [
  {
    icon: 'material-symbols-light:design-services-outline',
    href: '/builder',
    name: 'Resume Builder',
    authOnly: true,
  },
  {
    icon: 'material-symbols-light:work-outline',
    href: '/jobs',
    name: 'Job Board',
  },
  {
    icon: 'material-symbols-light:target',
    href: '/offer-recommendations',
    name: 'Job Match',
    authOnly: true,
  },
  {
    icon: 'material-symbols-light:apartment',
    href: '/company-profile',
    name: 'Companies',
  },
  {
    icon: 'material-symbols-light:credit-card-outline',
    href: '/pricing/candidate',
    name: 'Pricing',
  },
];

const AppSidebar = () => {
  const router = useRouter();
  const { data: profileData } = useProfile();

  const getIsAuthNeed = (authOnly?: boolean) => authOnly && !profileData;

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="flex-row items-center justify-between p-4 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:px-2">
        <Link href="/builder" className="text-jb-primary-500">
          <MindPalLogo
            viewBox="0 0 244 26"
            className="h-4 w-auto group-data-[collapsible=icon]:hidden"
          />
          <MindPalLogoIcon className="hidden h-5 w-8 group-data-[collapsible=icon]:block" />
        </Link>
        <SidebarTrigger />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {sidebarLinks.map(({ href, icon, name, authOnly }) => (
              <SidebarMenuItem key={href.toString()}>
                <SidebarMenuButton
                  asChild
                  isActive={router.pathname.startsWith(href.toString())}
                  tooltip={name}
                  className="border border-jb-neutral-400"
                >
                  <Link
                    href={
                      getIsAuthNeed(authOnly)
                        ? {
                            pathname: '/candidate-auth/signup',
                            query: { callback: href.toString() },
                          }
                        : href
                    }
                    className="relative"
                  >
                    <Icon icon={icon} />
                    <Text
                      variant="jb-body-medium"
                      className="truncate text-jb-neutral-700"
                    >
                      {name}
                    </Text>
                    {getIsAuthNeed(authOnly) && (
                      <span className="pointer-events-none absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-0.5 rounded-full bg-jb-primary-100 px-2 py-0.5 font-inter text-jb-body-xsmall font-medium text-jb-primary-400 group-data-[collapsible=icon]:right-0 group-data-[collapsible=icon]:top-0 group-data-[collapsible=icon]:translate-y-0 group-data-[collapsible=icon]:p-px">
                        <Icon
                          icon="material-symbols-light:lock-outline"
                          fontSize={12}
                        />
                        <span className="group-data-[collapsible=icon]:hidden">
                          Sign up
                        </span>
                      </span>
                    )}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        {profileData ? <UserMenu name={profileData.name} /> : <GuestMenu />}
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
};

export default AppSidebar;

const UserMenu = ({ name }: { name: string }) => {
  const router = useRouter();
  const { data: userCurrentSubscriptionData } = useUserCurrentSubscription();
  const { mutate: logout } = useLogout();

  return (
    <SidebarMenu>
      <MindCoinsMenuItem />
      <SidebarMenuItem>
        <div className="flex items-center gap-2 p-1 group-data-[collapsible=icon]:p-0">
          <Image
            alt=""
            src={ProfileDefaultImg}
            className="aspect-square w-8 shrink-0 rounded-full"
          />
          <Text
            as="span"
            variant="jb-body-medium"
            className="truncate font-medium text-jb-neutral-1000 group-data-[collapsible=icon]:hidden"
          >
            {name}
          </Text>
        </div>
      </SidebarMenuItem>
      {userCurrentSubscriptionData && (
        <SidebarMenuItem>
          <SidebarMenuButton asChild tooltip="Manage subscription">
            <Link
              href={{
                pathname: '/pricing/customer-portal',
                query: { callbackUrl: router.asPath },
              }}
            >
              <Icon icon="material-symbols-light:settings-outline" />
              <Text as="span" variant="jb-body-medium" className="truncate">
                Manage subscription
              </Text>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      )}
      <SidebarMenuItem>
        <Button
          variant="jbPrimary"
          size="sm"
          prefixIcon="material-symbols-light:logout"
          className="w-full"
          onClick={() => logout()}
        >
          <Text
            as="span"
            variant="jb-body-medium"
            className="truncate group-data-[collapsible=icon]:hidden"
          >
            Log out
          </Text>
        </Button>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};

const MIND_COINS_PACKAGE_AMOUNT = 30000;
const MIND_COINS_PRICE = '$2.99';
const MIND_COINS_DISCOUNTED_PRICE = '$0.90';
const MIND_COINS_PROMO_CODE = 'XSVPGLYF';
const MIND_COINS_PROMO_DISCOUNT = '70%';

const MindCoinsPromoCode = () => {
  const [isCopied, setIsCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(MIND_COINS_PROMO_CODE);
      setIsCopied(true);
      toast.success('Promo code copied');
      setTimeout(() => setIsCopied(false), 2000);
    } catch (_e) {
      toast.error('Something went wrong');
    }
  };

  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-dashed border-jb-primary-400 bg-jb-neutral-100 p-2">
      <div className="flex items-center justify-between gap-1">
        <div className="flex min-w-0 items-center gap-1">
          <Icon
            icon="material-symbols:sell-outline"
            className="shrink-0 text-jb-primary-400"
            fontSize={14}
          />
          <Text
            variant="jb-body-xsmall"
            className="truncate font-semibold text-jb-primary-500"
          >
            Limited-time promo code
          </Text>
        </div>
        <span className="shrink-0 rounded-full bg-jb-primary-500 px-2 py-0.5 font-inter text-jb-body-xsmall font-semibold text-jb-neutral-100">
          -{MIND_COINS_PROMO_DISCOUNT}
        </span>
      </div>
      <button
        type="button"
        onClick={onCopy}
        className="flex items-center justify-between gap-2 rounded-md bg-jb-primary-100 px-2 py-1.5 transition-colors hover:bg-jb-primary-200"
        aria-label="Copy promo code"
      >
        <Text
          as="span"
          variant="jb-body-small"
          className="truncate font-mono font-semibold tracking-wider text-jb-neutral-1000"
        >
          {MIND_COINS_PROMO_CODE}
        </Text>
        <Icon
          icon={
            isCopied
              ? 'material-symbols:check-rounded'
              : 'material-symbols:content-copy-outline-rounded'
          }
          className={isCopied ? 'text-jb-success-400' : 'text-jb-primary-500'}
          fontSize={16}
        />
      </button>
      <Text variant="jb-body-xsmall" className="text-jb-neutral-700">
        Apply it at checkout to get {MIND_COINS_PROMO_DISCOUNT} off your
        Mindcoins.
      </Text>
    </div>
  );
};

const MindCoinsMenuItem = () => {
  const { data: mindCoinsData } = useMindCoins();
  const { mutate, isLoading } = useMindCoinsCheckout();

  if (!mindCoinsData) return null;

  const { mindCoins } = mindCoinsData;
  const formattedMindCoins = mindCoins.toLocaleString('en-US');

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        tooltip={`${formattedMindCoins} MindCoins`}
        className="hidden group-data-[collapsible=icon]:flex"
      >
        <Icon icon="material-symbols:toll-rounded" />
      </SidebarMenuButton>
      <div className="flex flex-col gap-3 rounded-lg border border-jb-primary-200 bg-jb-primary-100 p-3 group-data-[collapsible=icon]:hidden">
        <div className="flex items-center gap-2">
          <Icon
            icon="material-symbols:toll-rounded"
            variant="candidatePrimary"
            className="shrink-0 p-1"
            fontSize={28}
          />
          <div className="flex min-w-0 flex-col">
            <Text variant="jb-body-small" className="text-jb-neutral-700">
              MindCoins
            </Text>
            <Text
              as="span"
              variant="jb-body-large"
              className="truncate font-semibold text-jb-neutral-1000"
            >
              {formattedMindCoins}
            </Text>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 rounded-lg bg-jb-neutral-100 px-2 py-1.5">
          <Text
            as="span"
            variant="jb-body-small"
            className="font-semibold text-jb-neutral-1000"
          >
            +{MIND_COINS_PACKAGE_AMOUNT.toLocaleString('en-US')} MindCoins
          </Text>
          <div className="flex shrink-0 items-baseline gap-1">
            <Text
              as="span"
              variant="jb-body-xsmall"
              className="text-jb-neutral-700 line-through"
            >
              {MIND_COINS_PRICE}
            </Text>
            <Text
              as="span"
              variant="jb-body-medium"
              className="font-bold text-jb-success-400"
            >
              {MIND_COINS_DISCOUNTED_PRICE}
            </Text>
          </div>
        </div>
        <MindCoinsPromoCode />
        <Button
          variant="jbSecondaryWithPrimaryBorder"
          size="sm"
          prefixIcon="material-symbols:add-rounded"
          iconClassName="h-4 w-4"
          className="w-full"
          onClick={() => mutate()}
          disabled={isLoading}
        >
          <Text as="span" variant="jb-body-small" className="font-medium">
            Buy more · {MIND_COINS_PRICE}
          </Text>
        </Button>
      </div>
    </SidebarMenuItem>
  );
};

const GuestMenu = () => {
  const router = useRouter();

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton asChild tooltip="Log in">
          <Link
            href={{
              pathname: '/candidate-auth/signin',
              query: { callback: router.asPath },
            }}
          >
            <Icon icon="material-symbols-light:login" />
            <Text as="span" variant="jb-body-medium" className="truncate">
              Log in
            </Text>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
      <SidebarMenuItem>
        <SidebarMenuButton asChild tooltip="Sign up">
          <Link
            href={{
              pathname: '/candidate-auth/signup',
              query: { callback: router.asPath },
            }}
          >
            <Icon icon="material-symbols-light:person-add-outline" />
            <Text as="span" variant="jb-body-medium" className="truncate">
              Sign up
            </Text>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};
